import React from 'react'

import Checkbox from '@mui/material/Checkbox'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'

import { InstitutionLogo } from '@mxenabled/mxui'

import { ConnectCard, Drawer, useGlobalCopyStore, useAppConfig } from '../../common'
import { Account } from '../../core'
import getAccountDisplayName from '../../common/utils/AccountDisplayName'
import { formatCurrency } from '../../core/utils/NumberFormatting'

interface AccountSelectionDrawerProps {
  accounts: Account[]
  isOpen: boolean
  onClose: () => void
  onSave: (accounts: Account[]) => void
  title: string
}

export const AccountSelectionDrawer = ({
  accounts,
  isOpen,
  onClose,
  onSave,
  title,
}: AccountSelectionDrawerProps) => {
  const { goals: copy, common: commonCopy } = useGlobalCopyStore()
  const { config } = useAppConfig()

  const [selectedAccounts, setSelectedAccounts] = React.useState<Account[]>([])

  const handleClick = (account: Account) => {
    const index = selectedAccounts.findIndex((a) => a.guid === account.guid)
    const updatedSelected = [...selectedAccounts]
    if (index >= 0) {
      updatedSelected.splice(index, 1)
    } else {
      updatedSelected.push(account)
    }
    setSelectedAccounts(updatedSelected)
  }

  return (
    <Drawer
      ariaLabelClose={commonCopy.close_aria}
      isOpen={isOpen}
      onClose={onClose}
      onPrimaryAction={() => onSave(selectedAccounts)}
      title={title}
    >
      <List sx={{ bgcolor: 'background.white' }}>
        <Divider variant="fullWidth" />
        {accounts.map((account, index) => (
          <React.Fragment key={account.guid}>
            <ListItem>
              <ListItemButton onClick={() => handleClick(account)}>
                <ListItemAvatar>
                  <InstitutionLogo
                    alt={account?.name || ''}
                    institutionGuid={account?.institution_guid || ''}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={getAccountDisplayName(account, {
                    allowNickname: config?.show_account_nicknames_in_master,
                  })}
                  secondary={formatCurrency(account.balance || 0, '0,0.00')}
                />
                <Checkbox checked={selectedAccounts.some((a) => a.guid === account.guid)} />
              </ListItemButton>
            </ListItem>
            <Divider variant={index < accounts.length - 1 ? 'inset' : 'fullWidth'} />
          </React.Fragment>
        ))}
      </List>

      {config.show_connections_widget_in_master && (
        <ConnectCard
          description={copy.connect_description}
          label={copy.connect_button}
          title={copy.connect_title}
        />
      )}
    </Drawer>
  )
}
